import React, { useState, useEffect, useCallback } from 'react';
import { useSessionState } from '../hooks/useSessionState';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCurrentModule } from '../contexts/ModuleContext';
import { Layout } from '../components/Layout';
import { StatusBadge, PriorityBadge } from '../components/StatusBadge';
import { formatDate } from '../utils/dateUtils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Search, Plus, Ticket, Filter, Edit, Copy, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import DepartmentSelectorModal from '../components/DepartmentSelectorModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MyTicketsPage() {
  const navigate = useNavigate();
  const { getAuthHeader, employee } = useAuth();
  const role = employee?.role || '';
  const isITPrimary = role === 'IT Team' || role === 'IT Head';
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useSessionState('my-tickets:search', '');
  const [statusFilter, setStatusFilter] = useSessionState('my-tickets:statusFilter', 'all');
  const [activeTab, setActiveTab] = useSessionState('my-tickets:activeTab', 'all');
  const [moduleFilter, setModuleFilter] = useSessionState('my-tickets:module', isITPrimary ? 'IT' : 'Admin');

  // Sync to global header
  const { setCurrentModule } = useCurrentModule();
  useEffect(() => { setCurrentModule(moduleFilter); }, [moduleFilter, setCurrentModule]);
  const [duplicating, setDuplicating] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deptModalOpen, setDeptModalOpen] = useState(false);

  const handleDuplicate = async (e, ticketId) => {
    e.stopPropagation(); // Prevent row click
    setDuplicating(ticketId);
    try {
      const res = await fetch(`${API}/tickets/${ticketId}/duplicate`, {
        method: 'POST',
        headers: getAuthHeader(),
        
      });
      if (res.ok) {
        const data = await res.json();
        toast.success('Ticket duplicated! Redirecting to draft...');
        navigate(`/tickets/${data.ticket.ticket_id}`);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to duplicate ticket');
      }
    } catch (error) {
      toast.error('Failed to duplicate ticket');
    } finally {
      setDuplicating(null);
    }
  };

  const handleDeleteDraft = async (e, ticketId) => {
    e.stopPropagation(); // Prevent row click
    if (!window.confirm('Are you sure you want to delete this draft?')) {
      return;
    }
    setDeleting(ticketId);
    try {
      const res = await fetch(`${API}/tickets/${ticketId}`, {
        method: 'DELETE',
        headers: getAuthHeader(),
        
      });
      if (res.ok) {
        toast.success('Draft deleted');
        // Refresh tickets list
        fetchTickets();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to delete draft');
      }
    } catch (error) {
      toast.error('Failed to delete draft');
    } finally {
      setDeleting(null);
    }
  };

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/tickets?my_tickets=true`, {
        headers: getAuthHeader(),
        
      });

      if (res.ok) {
        const data = await res.json();
        setTickets(data);
      }
    } catch (error) {
      console.error('Fetch tickets error:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch = 
      ticket.ticket_id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket.request_type.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket.description.toLowerCase().includes(searchQuery.toLowerCase());

    let matchesStatus = true;
    if (statusFilter !== 'all') {
      matchesStatus = ticket.status === statusFilter;
    }

    let matchesTab = true;
    if (activeTab === 'draft') {
      matchesTab = ticket.status === 'Draft';
    } else if (activeTab === 'pending') {
      matchesTab = ticket.status === 'Open';
    } else if (activeTab === 'active') {
      matchesTab = ['Approved', 'In Progress'].includes(ticket.status);
    } else if (activeTab === 'completed') {
      matchesTab = ticket.status === 'Completed';
    } else if (activeTab === 'rejected') {
      matchesTab = ticket.status === 'Rejected';
    } else if (activeTab === 'cancelled') {
      matchesTab = ticket.status === 'Cancelled';
    }

    const matchesModule = (ticket.module || 'Admin') === moduleFilter;

    return matchesSearch && matchesStatus && matchesTab && matchesModule;
  });

  const TicketRow = ({ ticket }) => (
    <TableRow 
      className="table-row-hover cursor-pointer"
      onClick={() => navigate(`/tickets/${ticket.ticket_id}`)}
      data-testid={`ticket-row-${ticket.ticket_id}`}
    >
      <TableCell>
        <span className="ticket-id-link font-mono text-sm">
          {ticket.ticket_id}
        </span>
      </TableCell>
      <TableCell className="font-medium max-w-[200px] truncate">
        {ticket.request_type === 'Other' 
          ? ticket.other_request_type 
          : ticket.request_type}
      </TableCell>
      <TableCell className="hidden md:table-cell text-muted-foreground">
        {ticket.department}
      </TableCell>
      <TableCell className="hidden sm:table-cell">
        <PriorityBadge priority={ticket.priority} />
      </TableCell>
      <TableCell>
        <StatusBadge status={ticket.status} />
      </TableCell>
      <TableCell className="hidden lg:table-cell text-muted-foreground text-sm">
        {formatDate(ticket.created_at)}
      </TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-1">
          {/* Edit button - only for Draft or Open status */}
          {['Draft', 'Open'].includes(ticket.status) && (
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/tickets/${ticket.ticket_id}/edit`);
              }}
              title="Edit ticket"
              data-testid={`edit-ticket-${ticket.ticket_id}`}
            >
              <Edit className="w-4 h-4" />
            </Button>
          )}
          {/* Duplicate button */}
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => handleDuplicate(e, ticket.ticket_id)}
            disabled={duplicating === ticket.ticket_id}
            title="Duplicate ticket"
            data-testid={`duplicate-ticket-${ticket.ticket_id}`}
          >
            {duplicating === ticket.ticket_id ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
          </Button>
          {/* Delete button - only for Draft status */}
          {ticket.status === 'Draft' && (
            <Button
              size="sm"
              variant="ghost"
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={(e) => handleDeleteDraft(e, ticket.ticket_id)}
              disabled={deleting === ticket.ticket_id}
              title="Delete draft"
              data-testid={`delete-ticket-${ticket.ticket_id}`}
            >
              {deleting === ticket.ticket_id ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );

  return (
    <Layout>
      <div className="space-y-6" data-testid="my-tickets-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">My Tickets</h1>
            <p className="text-sm sm:text-base text-muted-foreground mt-1">
              View and track all your submitted requests
            </p>
          </div>
          <Button 
            onClick={() => setDeptModalOpen(true)}
            className="gap-2 btn-press"
            data-testid="create-new-ticket-btn"
          >
            <Plus className="w-4 h-4" />
            New Ticket
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-4">
          <div className="relative flex-1 sm:max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search tickets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="search-tickets-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-[160px]" data-testid="status-filter-select">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filter status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="Draft">Draft</SelectItem>
              <SelectItem value="Open">Open</SelectItem>
              <SelectItem value="Approved">Approved</SelectItem>
              <SelectItem value="In Progress">In Progress</SelectItem>
              <SelectItem value="Completed">Completed</SelectItem>
              <SelectItem value="Rejected">Rejected</SelectItem>
              <SelectItem value="Cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>

          {/* Department slicer (Admin / IT) — persists in session */}
          <div className="flex items-center gap-1 border rounded-md p-1 bg-card h-9" data-testid="my-tickets-module-slicer">
            {[
              { v: 'Admin', l: 'Admin' },
              { v: 'IT', l: 'IT' },
            ].map(opt => (
              <button
                key={opt.v}
                type="button"
                onClick={() => setModuleFilter(opt.v)}
                data-testid={`my-tickets-module-${opt.v.toLowerCase()}`}
                className={`px-2.5 py-0.5 text-xs rounded font-medium transition ${
                  moduleFilter === opt.v ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'
                }`}
              >
                {opt.l}
              </button>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
            <TabsTrigger value="draft" data-testid="tab-draft">Drafts</TabsTrigger>
            <TabsTrigger value="pending" data-testid="tab-pending">Pending</TabsTrigger>
            <TabsTrigger value="active" data-testid="tab-active">Active</TabsTrigger>
            <TabsTrigger value="completed" data-testid="tab-completed">Completed</TabsTrigger>
            <TabsTrigger value="rejected" data-testid="tab-rejected">Rejected</TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-4">
            <Card>
              <CardContent className="p-0">
                {loading ? (
                  <div className="p-6 space-y-3">
                    {[1, 2, 3, 4, 5].map(i => (
                      <Skeleton key={i} className="h-12 w-full" />
                    ))}
                  </div>
                ) : filteredTickets.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground empty-state">
                    <Ticket className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p className="font-medium">No tickets found</p>
                    <p className="text-sm mt-1">
                      {searchQuery || statusFilter !== 'all'
                        ? 'Try adjusting your filters'
                        : 'Create a new ticket to get started'}
                    </p>
                    {!searchQuery && statusFilter === 'all' && (
                      <Button 
                        variant="link" 
                        onClick={() => setDeptModalOpen(true)}
                        className="mt-2"
                      >
                        Create your first ticket
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="rounded-md border-0 overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/50">
                          <TableHead>Ticket ID</TableHead>
                          <TableHead>Request Type</TableHead>
                          <TableHead className="hidden md:table-cell">Department</TableHead>
                          <TableHead className="hidden sm:table-cell">Priority</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="hidden lg:table-cell">Date</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredTickets.map((ticket) => (
                          <TicketRow key={ticket.ticket_id} ticket={ticket} />
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      <DepartmentSelectorModal open={deptModalOpen} onOpenChange={setDeptModalOpen} />
    </Layout>
  );
}
